import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { useSharedStyles } from "./sharedStyles";
import {
  BEDINGUNGEN_ALLGEMEIN_TEXT,
  BEDINGUNGEN_BESONDERE_TEXT,
} from "./characteristicKeys";
import FileDropzone, { type UploadedFile } from "./FileDropzone";

// ── Tile primitive ──
const useTileStyles = makeStyles({
  tile: {
    cursor: "pointer",
    appearance: "none",
    textAlign: "left",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    minHeight: "70px",
    transitionProperty: "border-color, background-color, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
  },
  tileActive: {
    ...shorthands.border("2px", "dashed", BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: "0 4px 14px rgba(77,32,81,0.18)",
  },
  title: { fontWeight: 700, fontSize: "0.95rem", color: BRAND.ink },
  hint: { fontSize: "0.78rem", color: BRAND.muted },
});

const Tile = ({
  active,
  title,
  hint,
  onClick,
}: {
  active: boolean;
  title: string;
  hint?: string;
  onClick: () => void;
}) => {
  const styles = useTileStyles();
  return (
    <button
      type="button"
      className={mergeClasses(styles.tile, active && styles.tileActive)}
      onClick={onClick}
    >
      <span className={styles.title}>{title}</span>
      {hint && <span className={styles.hint}>{hint}</span>}
    </button>
  );
};

const usePanelStyles = makeStyles({
  panel: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  panelTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  },
  banner: {
    backgroundColor: "rgba(247,168,35,0.18)",
    color: "#7a4c00",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.gold),
  },
  removeBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: "#a1331c",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    ...shorthands.border("0"),
    textDecorationLine: "underline",
  },
  addBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    alignSelf: "flex-start",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "dashed", BRAND.purple),
    ":hover": { backgroundColor: "#f5eef6" },
  },
});

// ── BedingungenDokumenteBlock ────────────────────────────────────────
export type BedingungBlockState = { text: string; file: UploadedFile | null };

export const BedingungenDokumenteBlock = ({
  allgemein,
  besondere,
  onChangeAllgemein,
  onChangeBesondere,
}: {
  allgemein: BedingungBlockState;
  besondere: BedingungBlockState;
  onChangeAllgemein: (next: BedingungBlockState) => void;
  onChangeBesondere: (next: BedingungBlockState) => void;
}) => {
  const card = useSharedStyles();
  const panel = usePanelStyles();
  return (
    <div className={card.cardSection}>
      <div className={panel.panel}>
        <h4 className={panel.panelTitle}>Allgemeine Bedingungen</h4>
        <div className={card.field}>
          <label className={card.label}>Bedingungstext</label>
          <textarea
            className={card.textarea}
            value={allgemein.text}
            onChange={(e) => onChangeAllgemein({ ...allgemein, text: e.target.value })}
            placeholder={BEDINGUNGEN_ALLGEMEIN_TEXT}
          />
        </div>
        <div className={card.field}>
          <label className={card.label}>Dokument</label>
          <FileDropzone
            label="Allgemeine-Bedingungen-Dokument auswählen"
            hint="PDF, Word oder Bild — wird mit dem Vertragsentwurf gespeichert."
            file={allgemein.file}
            onChange={(f) => onChangeAllgemein({ ...allgemein, file: f })}
          />
        </div>
      </div>
      <div className={panel.panel}>
        <h4 className={panel.panelTitle}>Besondere Bedingungen</h4>
        <div className={card.field}>
          <label className={card.label}>Bedingungstext</label>
          <textarea
            className={card.textarea}
            value={besondere.text}
            onChange={(e) => onChangeBesondere({ ...besondere, text: e.target.value })}
            placeholder={BEDINGUNGEN_BESONDERE_TEXT}
          />
        </div>
        <div className={card.field}>
          <label className={card.label}>Dokument</label>
          <FileDropzone
            label="Besondere-Bedingungen-Dokument auswählen"
            hint="PDF, Word oder Bild — wird mit dem Vertragsentwurf gespeichert."
            file={besondere.file}
            onChange={(f) => onChangeBesondere({ ...besondere, file: f })}
          />
        </div>
      </div>
    </div>
  );
};

// ── PhotovoltaikBlock ────────────────────────────────────────
export const PhotovoltaikBlock = ({
  vorhanden,
  policennr,
  onChange,
}: {
  vorhanden: string;
  policennr: string;
  onChange: (k: "vorhanden" | "policennr", v: string) => void;
}) => {
  const card = useSharedStyles();
  return (
    <div className={card.cardSection}>
      <div className={card.helper}>
        Besteht für das Objekt eine separate Photovoltaikanlagen-Versicherung?
      </div>
      <div className={card.tileGrid2}>
        <Tile active={vorhanden === "ja"} title="Ja" onClick={() => onChange("vorhanden", "ja")} />
        <Tile active={vorhanden === "nein"} title="Nein" onClick={() => onChange("vorhanden", "nein")} />
      </div>
      {vorhanden === "ja" && (
        <div className={card.field} style={{ maxWidth: 360 }}>
          <label className={card.label}>Policennummer</label>
          <input
            className={card.input}
            type="text"
            value={policennr}
            onChange={(e) => onChange("policennr", e.target.value)}
            placeholder="Policennummer der separaten Photovoltaik-Police"
          />
        </div>
      )}
    </div>
  );
};

// ── VolldeckungPraemienBlock — nur bei Fremdvertrag ────────────────────────
export type VolldeckungPraemienState = {
  ab: string;
  praemieDerzeit: string;
  praemieVolldeckung: string;
  praemieDifferenz: string;
};

export const VolldeckungPraemienBlock = ({
  state,
  onPatch,
}: {
  state: VolldeckungPraemienState;
  onPatch: (patch: Partial<VolldeckungPraemienState>) => void;
}) => {
  const card = useSharedStyles();
  const panel = usePanelStyles();
  return (
    <div className={panel.panel}>
      <h4 className={panel.panelTitle}>Volldeckung & Prämienberechnung</h4>
      <div className={card.helper}>
        Wird nur bei Fremdvertrag erfasst — beschreibt den Übergang Differenzdeckung → Volldeckung.
      </div>
      <div className={card.field} style={{ maxWidth: 280 }}>
        <label className={card.label}>Volldeckung ab</label>
        <input
          className={card.input}
          type="date"
          value={state.ab}
          onChange={(e) => onPatch({ ab: e.target.value })}
        />
      </div>
      <div className={card.threeCol}>
        <div className={card.field}>
          <label className={card.label}>Prämie derzeit (€)</label>
          <input
            className={card.input}
            type="number"
            value={state.praemieDerzeit}
            onChange={(e) => onPatch({ praemieDerzeit: e.target.value })}
          />
        </div>
        <div className={card.field}>
          <label className={card.label}>Prämie Volldeckung (€)</label>
          <input
            className={card.input}
            type="number"
            value={state.praemieVolldeckung}
            onChange={(e) => onPatch({ praemieVolldeckung: e.target.value })}
          />
        </div>
        <div className={card.field}>
          <label className={card.label}>Prämie während Differenzdeckung (€)</label>
          <input
            className={card.input}
            type="number"
            value={state.praemieDifferenz}
            onChange={(e) => onPatch({ praemieDifferenz: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

// ── BemerkungenHinweiseBlock (combined) ────────────────────────────────────────
export type Hinweis = { klausel: string; abgestimmtMit: string; abgestimmtAm: string };

const BAUJAHRNACHLASS_NOTE = "Baujahrnachlass wird nicht dokumentiert.";

export const BemerkungenHinweiseBlock = ({
  bemerkung,
  onChangeBemerkung,
  hinweise,
  onChangeHinweise,
  mietverlustKomplettausschluss,
}: {
  bemerkung: string;
  onChangeBemerkung: (v: string) => void;
  hinweise: Hinweis[];
  onChangeHinweise: (next: Hinweis[]) => void;
  mietverlustKomplettausschluss: boolean;
}) => {
  const card = useSharedStyles();
  const panel = usePanelStyles();
  const updateRow = (idx: number, patch: Partial<Hinweis>) => {
    onChangeHinweise(hinweise.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  return (
    <div className={card.cardSection}>
      <div className={panel.panel}>
        <h4 className={panel.panelTitle}>Bemerkungen</h4>
        <div className={card.field}>
          <label className={card.label}>Freitext</label>
          <textarea
            className={card.textarea}
            value={bemerkung}
            onChange={(e) => onChangeBemerkung(e.target.value)}
            placeholder="Freitext für Bemerkungen zum Vertrag…"
          />
        </div>
      </div>

      {mietverlustKomplettausschluss && (
        <div className={panel.banner}>
          Hinweis: „Mietverlust temporär vermietete Objekte – Beitragsreduzierung in Gebäude 30%
          (Komplettausschluss Mietverlust)" – abgestimmt mit Hr. Christoph Müller (17.04.2024).
        </div>
      )}

      <div className={panel.panel}>
        <h4 className={panel.panelTitle}>Besondere Hinweise für den Versicherer</h4>
        {hinweise.map((r, idx) => (
          <div key={idx} className={card.threeCol}>
            <div className={card.field}>
              <label className={card.label}>Klausel / Hinweis</label>
              <textarea
                className={card.textarea}
                value={r.klausel}
                onChange={(e) => updateRow(idx, { klausel: e.target.value })}
              />
            </div>
            <div className={card.field}>
              <label className={card.label}>abgestimmt mit</label>
              <input
                className={card.input}
                type="text"
                value={r.abgestimmtMit}
                onChange={(e) => updateRow(idx, { abgestimmtMit: e.target.value })}
              />
            </div>
            <div className={card.field}>
              <label className={card.label}>am</label>
              <input
                className={card.input}
                type="date"
                value={r.abgestimmtAm}
                onChange={(e) => updateRow(idx, { abgestimmtAm: e.target.value })}
              />
              <button
                type="button"
                className={panel.removeBtn}
                style={{ marginTop: 6, alignSelf: "flex-start" }}
                onClick={() => onChangeHinweise(hinweise.filter((_, i) => i !== idx))}
              >
                Entfernen
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className={panel.addBtn}
          onClick={() => onChangeHinweise([...hinweise, { klausel: "", abgestimmtMit: "", abgestimmtAm: "" }])}
        >
          + Klausel hinzufügen
        </button>
      </div>
      <div className={panel.banner}>{BAUJAHRNACHLASS_NOTE}</div>
    </div>
  );
};

// ── SummaryBlock ────────────────────────────────────────
const useSummaryStyles = makeStyles({
  list: { display: "flex", flexDirection: "column", rowGap: "6px" },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "12px",
    paddingTop: "6px",
    paddingBottom: "6px",
    fontSize: "0.9rem",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  rowKey: { color: BRAND.muted },
  rowValue: { color: BRAND.ink, fontWeight: 600 },
});

export const SummaryBlock = ({ rows }: { rows: Array<{ key: string; value: string }> }) => {
  const card = useSharedStyles();
  const panel = usePanelStyles();
  const styles = useSummaryStyles();
  return (
    <div className={card.cardSection}>
      <div className={card.helper}>
        Bitte prüfen Sie alle Angaben vor dem Anlegen des Vertrages.
      </div>
      <div className={panel.panel}>
        <div className={styles.list}>
          {rows.map((r, i) => (
            <div key={i} className={styles.row}>
              <span className={styles.rowKey}>{r.key}</span>
              <span className={styles.rowValue}>{r.value || "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
